/**
 * Use case: adjust a customer's balance with money-safety guarantees.
 * Generates a fresh idempotency key per attempt and validates the input
 * before going to the network.
 */
import type { CustomerDetail } from '@domain/customer/Customer';
import type {
  BalanceAdjustment,
  CustomerRepository,
} from '@domain/customer/CustomerRepository';

export class InvalidAdjustment extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'InvalidAdjustment';
  }
}

export interface AdjustBalanceInput {
  amount: string; // signed string — '+25.00' or '-10.00'
  reason: string;
  idempotencyKey?: string; // optional override for retries with same key
}

export const adjustBalance = (repo: CustomerRepository) =>
  async (
    customer: Pick<CustomerDetail, 'id'>,
    input: AdjustBalanceInput,
  ): Promise<CustomerDetail> => {
    const reason = (input.reason || '').trim();
    if (!reason) throw new InvalidAdjustment('reason is required');
    const amt = Number(input.amount);
    if (!Number.isFinite(amt) || amt === 0) {
      throw new InvalidAdjustment('amount must be a non-zero number');
    }
    const adjustment: BalanceAdjustment = {
      amount: input.amount,
      reason,
      idempotency_key: input.idempotencyKey ?? crypto.randomUUID(),
    };
    return repo.adjustBalance(customer.id, adjustment);
  };
